import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Phone, Calendar, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [password, setPassword] = useState(() => sessionStorage.getItem("madeira_admin_pwd") || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ leads: [], contacts: [] });
  const [tab, setTab] = useState("leads");

  const login = async (e) => {
    e?.preventDefault();
    if (!password) return toast.error("Podaj hasło");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": password } });
      sessionStorage.setItem("madeira_admin_pwd", password);
      setAuthed(true);
      await loadData(password);
    } catch {
      toast.error("Niepoprawne hasło");
      sessionStorage.removeItem("madeira_admin_pwd");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (pwd) => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Password": pwd } });
      setData(data);
    } catch {
      toast.error("Błąd pobierania danych");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("madeira_admin_pwd");
    setPassword("");
    setAuthed(false);
    setData({ leads: [], contacts: [] });
  };

  useEffect(() => {
    if (password && !authed) {
      // try silent login if cached
      (async () => {
        try {
          await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": password } });
          setAuthed(true);
          await loadData(password);
        } catch { sessionStorage.removeItem("madeira_admin_pwd"); setPassword(""); }
      })();
    }
    // eslint-disable-next-line
  }, []);

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-deep px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm bg-navy border border-navy-line rounded-2xl p-8 shadow-2xl">
          <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-gold" />
          </div>
          <h1 className="font-display text-3xl text-cream mb-1">Panel admina</h1>
          <p className="text-sm text-cream/60 mb-6">Wpisz hasło dostępu</p>
          <Label className="text-xs uppercase tracking-wider text-cream/70">Hasło</Label>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 mb-5 bg-navy-deep/60 border-navy-line text-cream"
            autoFocus
          />
          <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="btn-primary w-full py-5">
            {loading ? "Logowanie..." : "Zaloguj"}
          </Button>
        </form>
      </div>
    );
  }

  const items = tab === "leads" ? data.leads : data.contacts;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy-deep text-cream">
      <header className="border-b border-navy-line">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl">Panel admina – Madeira.com.pl</h1>
            <p className="text-xs text-cream/55">Leady i wiadomości kontaktowe</p>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="admin-refresh-btn" onClick={() => loadData(password)} variant="ghost" className="text-cream hover:bg-navy-mid">
              <RefreshCw className="w-4 h-4 mr-2" /> Odśwież
            </Button>
            <Button data-testid="admin-logout-btn" onClick={logout} variant="ghost" className="text-cream hover:bg-navy-mid">
              <LogOut className="w-4 h-4 mr-2" /> Wyloguj
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "leads" ? "bg-gold text-navy-deep" : "bg-navy border border-navy-line text-cream/80 hover:text-cream"}`}
          >
            Leady ({data.leads.length})
          </button>
          <button
            data-testid="tab-contacts"
            onClick={() => setTab("contacts")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "contacts" ? "bg-gold text-navy-deep" : "bg-navy border border-navy-line text-cream/80 hover:text-cream"}`}
          >
            Wiadomości ({data.contacts.length})
          </button>
        </div>

        {items.length === 0 ? (
          <div className="bg-navy border border-navy-line rounded-2xl p-12 text-center text-cream/55">
            Brak rekordów w tej zakładce.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`record-${it.id}`} className="bg-navy border border-navy-line rounded-xl p-5 hover:border-gold/40 transition">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display text-xl">{it.name}</div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-cream/70">
                      <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5"/>{it.email}</span>
                      {it.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5"/>{it.phone}</span>}
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>{new Date(it.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {tab === "leads" ? (
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold">{it.trip_type}</span>
                      <span className="px-2.5 py-1 rounded-full bg-navy-mid border border-navy-line">{it.timeline}</span>
                      <span className="px-2.5 py-1 rounded-full bg-navy-mid border border-navy-line">{it.budget}</span>
                    </div>
                  ) : (
                    it.message && <div className="w-full mt-2 text-sm text-cream/80 bg-navy-deep/60 rounded-lg p-3 border border-navy-line">{it.message}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
